/**
 * LOADING AND PRE-TICKING FOR THE ATTACHMENT PICKER — `tasks/practice-documents.md` §14.3, §24.2.
 *
 * ── WHY THE LOAD IS HERE AND NOT IN THE COMPONENT ────────────────────────────────────────────
 *
 * Two things need it. The chip row loads so it can render *"Pre-operative instructions — 3 days
 * before the appointment"* before anybody clicks anything, and `choose()` loads for a host that has
 * no chip row at all: §14.3's WhatsApp host is a MENU ITEM beside "Prescription" and
 * "Invoice/Receipt", and a menu item opens a dialog — it has nowhere to put a chip row and nothing
 * to hand the dialog.
 *
 * Two callers of the same rules is exactly how the `sendSheet` duplication of §14.1 happened. One
 * `load`, one `preTickedIds`, and the dialog is handed its rows rather than fetching its own.
 *
 * ── THE REVERSE LINK ENDPOINT LIVES HERE, NOT IN `practice-document.service.ts` ───────────────
 *
 * That file says so in as many words: `GET /appointment-types/:id/practice-documents` "belongs to
 * the screen that books an appointment, and a settings service that offered it would make the
 * booking screen a consumer of the settings tab's service". This IS that consumer, so the call
 * lives with it. Nothing in `core/services/` changed.
 */

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, catchError, map, of, switchMap } from 'rxjs';

import { sendOnPhrase } from '../../../group/components/practice-document-settings/practice-document-copy';
import {
  PracticeDocumentDto, PracticeDocumentService,
} from '../../../core/services/practice-document.service';
import {
  DocumentAttachmentDialogComponent,
} from './document-attachment-dialog.component';
import {
  DocumentAttachmentDialogData,
  DocumentAttachmentDialogResult,
  DocumentAttachmentLoadOptions,
  DocumentAttachmentSource,
  DocumentAttachmentTriggerMap,
  LOAD_FAILED,
  TRIGGERS_FAILED,
  isOfferable,
  preTickedIds,
} from './document-attachment';

/**
 * One row of `GET /api/appointment-types/:appointmentTypeId/practice-documents` (§24.2).
 *
 * EXPORTED because the booking screen needs the raw `sendOn` / `offsetDays` / link `id`, not the
 * phrase this service collapses them into. §23.1.1's prompt has to work out which links name a
 * day that has already passed, and then name those links back to the server as
 * `documentDecisions: [{ linkId, action: 'send_now' }]` — neither of which a display string can
 * answer.
 */
export interface AppointmentTypeDocumentLink {
  id: string;
  practiceDocumentId: string;
  name?: string;
  requiresResponse?: boolean;
  sendOn: string;
  offsetDays: number | null;
  active?: boolean;
}

@Injectable({ providedIn: 'root' })
export class DocumentAttachmentService {

  constructor(
    private practiceDocumentService: PracticeDocumentService,
    private http: HttpClient,
    private dialog: MatDialog,
  ) { }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Loading
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * Everything one context needs, in one emission. NEVER errors: a failure is a field on the
   * result, because "we could not read them" is a thing the user has to be told rather than a
   * thing that unsubscribes the stream.
   */
  load(options: DocumentAttachmentLoadOptions): Observable<DocumentAttachmentSource> {
    return this.loadTriggers(options).pipe(
      switchMap((triggers) => this.practiceDocumentService
        .listAttachableDocuments(options.groupId)
        .pipe(
          map((response) => {
            const rows: PracticeDocumentDto[] = (response && response.data) ? response.data : [];
            return {
              documents: rows.filter((row) => isOfferable(row, options.context)),
              triggers: triggers.triggers,
              loadError: null,
              triggerError: triggers.error,
            } as DocumentAttachmentSource;
          }),
          // An empty list would say "this practice has no documents", which is a different and
          // much more misleading thing than "we could not load them" — and, since wave 1, the
          // difference decides whether the whole control is drawn at all.
          catchError(() => of({
            documents: [], triggers: {}, loadError: LOAD_FAILED, triggerError: null,
          } as DocumentAttachmentSource)),
        )),
    );
  }

  /**
   * The appointment type's ACTIVE links, turned into the phrase a chip shows.
   *
   * §24.2: this endpoint already returns active links of ACTIVE documents only, so there is no
   * second filter here — but `active` is re-checked because a link the server one day returns
   * paused is a link that must not silently pre-tick a document.
   */
  /**
   * §23.1.1 — THE RAW LINKS, for the booking screen's late-document prompt.
   *
   * The same endpoint `loadTriggers` reads, exposed without the collapse into a phrase. One fetch
   * implementation and one URL: a second copy of this call is a second thing to keep in step with
   * the route, and the route's own comment calls it "THE BOOKING SCREEN's read".
   *
   * ACTIVE links only, matching `loadTriggers`. A paused link must not raise a prompt any more
   * than it may pre-tick a document.
   *
   * A failed read resolves to `[]` rather than erroring: the prompt is an ADVISORY step before a
   * booking, and a receptionist with a patient in front of them must not be blocked from saving
   * because a trigger read timed out. The server measures lateness independently and will still
   * mark the row `skipped`, which is the visible outcome §23.1 exists to guarantee.
   */
  loadAppointmentTypeLinks(
    appointmentTypeId: string, groupId?: string | null,
  ): Observable<AppointmentTypeDocumentLink[]> {
    if (!appointmentTypeId) return of([]);
    return this.fetchLinks(appointmentTypeId, groupId).pipe(catchError(() => of([])));
  }

  /**
   * The one GET. Both readers go through it.
   *
   * `groupId` is REQUIRED by the server (`tasks/practice-documents.md` §32) — the practice this
   * booking belongs to. Without it the feed answers `400 GROUP_ID_REQUIRED`, and because the
   * public reader swallows errors into `of([])`, that would show as "this appointment type
   * attaches no documents" rather than as a failure. Hence it is threaded, not defaulted.
   */
  private fetchLinks(
    appointmentTypeId: string, groupId?: string | null,
  ): Observable<AppointmentTypeDocumentLink[]> {
    const url = `api/appointment-types/${appointmentTypeId}/practice-documents`;
    return this.http.get<any>(url, {
      headers: this.authHeaders(),
      params: new HttpParams().set('groupId', groupId ? String(groupId) : ''),
    }).pipe(
      map((response) => {
        const rows: AppointmentTypeDocumentLink[] =
          (response && Array.isArray(response.data)) ? response.data : [];
        return rows.filter((row) => row && row.practiceDocumentId && row.active !== false);
      }),
    );
  }

  private loadTriggers(options: DocumentAttachmentLoadOptions)
    : Observable<{ triggers: DocumentAttachmentTriggerMap; error: string | null }> {
    if (options.context !== 'appointment' || !options.appointmentTypeId) {
      return of({ triggers: {}, error: null });
    }

    return this.fetchLinks(options.appointmentTypeId).pipe(
      map((rows) => {
        const triggers: DocumentAttachmentTriggerMap = {};

        for (const row of rows) {
          // The `active` re-check that used to be here is `fetchLinks`', so both readers get it.
          const phrase = sendOnPhrase(row.sendOn, row.offsetDays);
          const already = triggers[row.practiceDocumentId];
          // §24.2: one appointment type legitimately holds SEVERAL links of the same document —
          // at confirmation AND three days before. A chip that showed only the first would tell a
          // receptionist the document goes once when it goes twice.
          if (!already) triggers[row.practiceDocumentId] = phrase;
          else if (already.indexOf(phrase) === -1) {
            triggers[row.practiceDocumentId] = `${already}, and ${phrase}`;
          }
        }
        return { triggers, error: null };
      }),
      // A trigger read that failed leaves the documents perfectly attachable BY HAND. Reporting
      // it as a load failure would hide a control the user can still use.
      catchError(() => of({ triggers: {} as DocumentAttachmentTriggerMap, error: TRIGGERS_FAILED })),
    );
  }

  /** Same header the rest of the app's services build. No `Content-Type`: these are GETs. */
  private authHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Pre-ticking, and the dialog
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /** §14.3, in one place. Re-exported here so a host never reaches past this service. */
  preTicked(source: DocumentAttachmentSource,
    options: DocumentAttachmentLoadOptions): string[] {
    return preTickedIds(source, options.context);
  }

  /**
   * OPEN THE DIALOG over rows somebody already has.
   *
   * `undefined` on close means CANCELLED, not "nothing selected" — a host that conflated the two
   * would untick everything every time a user pressed Escape.
   */
  open(data: DocumentAttachmentDialogData)
    : Observable<DocumentAttachmentDialogResult | undefined> {
    return this.dialog.open<DocumentAttachmentDialogComponent, DocumentAttachmentDialogData,
      DocumentAttachmentDialogResult>(DocumentAttachmentDialogComponent, {
        data,
        width: '560px',
        maxWidth: '95vw',
        autoFocus: false,
        restoreFocus: true,
      }).afterClosed();
  }

  /**
   * LOAD, PRE-TICK, AND OPEN — the whole control, for a host that has no room for a chip row.
   *
   * This is the shape §14.3's WhatsApp host needs: a menu item is clicked and a dialog appears
   * with the practice's documents in it, already ticked the way that context's configuration says.
   * Wave 2 calls this and gets a selection back; it never touches the picker component.
   *
   * An `initialSelection` wins over the configuration, for the same reason the chip row's does — a
   * user reopening the dialog has already chosen, and re-applying the configuration would discard
   * it silently.
   */
  choose(options: DocumentAttachmentLoadOptions & { initialSelection?: string[] })
    : Observable<DocumentAttachmentDialogResult | undefined> {
    return this.load(options).pipe(
      switchMap((source) => {
        const selection = (options.initialSelection && options.initialSelection.length)
          ? options.initialSelection.slice()
          : this.preTicked(source, options);

        return this.open({
          context: options.context,
          documents: source.documents,
          selection,
          triggers: source.triggers,
        });
      }),
    );
  }
}
